import { useEffect, useState, useCallback } from 'react';
import { apiFetch, apiImageUrl } from '../utils/streamApi';
import PatrolZoneEditor from '../components/PatrolZoneEditor';

// Employee Attendance — admin configuration. Define attendance checkpoints
// (camera + drawn zone + purpose + schedule + verification). Module OFF until enabled.

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']; // active_days 0=Mon..6=Sun
// Every camera is a single entry OR exit point. "Both" is not offered: a gate
// that does both clocks an employee out the next time they walk back through it.
const PURPOSE = [['checkin', 'Entry (Check-In)'], ['checkout', 'Exit (Check-Out)']];

const blank = () => ({
  name: '', camera_idx: '', zones: [], purpose: 'checkin',
  schedule: { start: '08:30', end: '19:00', active_days: [0, 1, 2, 3, 4] },
  min_presence_seconds: 1, exception_on_gesture: true, gesture_hold_seconds: 1.5, cooldown_seconds: 300,
  late_after: '', speaker_enabled: false, speaker_target: 'camera', speaker_name: true, active: true,
});

export default function AttendanceConfigPage() {
  const [cfg, setCfg]     = useState(null);
  const [opts, setOpts]   = useState({ cameras: [], employees: [], speakers: [], employee_dir: [] });
  const [draft, setDraft] = useState(null);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState('');
  const [emps, setEmps]   = useState([]);
  useEffect(() => { setEmps(opts.employee_dir || []); }, [opts.employee_dir]);
  const saveEmps = async next => { setEmps(next); await apiFetch('/ui/attendance/employees', { method: 'PUT', json: { employees: next } }); };

  const load = useCallback(async () => {
    const [c, o] = await Promise.all([
      apiFetch('/ui/attendance/config').then(r => r.ok ? r.json() : null),
      apiFetch('/ui/attendance/form-options').then(r => r.ok ? r.json() : null),
    ]);
    if (c) setCfg(c);
    if (o) setOpts(o);
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async patch => {
    setBusy(true);
    try { const r = await apiFetch('/ui/attendance/settings', { method: 'PUT', json: patch }); if (r.ok) { const u = await r.json(); setCfg(c => ({ ...c, ...u })); } }
    finally { setBusy(false); }
  };

  const startNew  = () => { setEditId(null); setDraft(blank()); };
  const startEdit = cp => { setEditId(cp.id); setDraft(JSON.parse(JSON.stringify({ ...blank(), ...cp }))); };
  const cancel    = () => { setDraft(null); setEditId(null); setErr(''); };

  const save = async () => {
    if (!draft.name.trim()) { setErr('Name is required.'); return; }
    if (draft.camera_idx === '' || draft.camera_idx === null) { setErr('Select a camera.'); return; }
    if (!draft.zones.length || draft.zones[0].length < 3) { setErr('Draw an attendance zone (3+ points).'); return; }
    setBusy(true); setErr('');
    const body = { ...draft, camera_idx: Number(draft.camera_idx) };
    try {
      const r = editId
        ? await apiFetch(`/ui/attendance/checkpoints/${editId}`, { method: 'PUT', json: body })
        : await apiFetch('/ui/attendance/checkpoints', { method: 'POST', json: body });
      if (r.ok) { cancel(); await load(); } else setErr('Save failed.');
    } catch { setErr('Save failed.'); } finally { setBusy(false); }
  };

  const del = async cp => {
    if (!window.confirm(`Delete attendance point "${cp.name}"?`)) return;
    setBusy(true);
    try { await apiFetch(`/ui/attendance/checkpoints/${cp.id}`, { method: 'DELETE' }); await load(); }
    finally { setBusy(false); }
  };

  const setD  = patch => setDraft(d => ({ ...d, ...patch }));
  const setS  = patch => setDraft(d => ({ ...d, schedule: { ...d.schedule, ...patch } }));
  const toggleDay = i => setDraft(d => {
    const days = d.schedule.active_days.includes(i) ? d.schedule.active_days.filter(x => x !== i) : [...d.schedule.active_days, i].sort((a, b) => a - b);
    return { ...d, schedule: { ...d.schedule, active_days: days } };
  });
  // one zone for v1 (backend supports many)
  const setZone = pts => setDraft(d => ({ ...d, zones: pts.length ? [pts] : [] }));   // keep every point; ≥3 enforced at save

  if (!cfg) return <div style={{ padding: 24, color: 'var(--text-muted)' }}>Loading Attendance…</div>;

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: '0 auto', color: 'var(--text)' }}>
      <h1 style={{ margin: '0 0 4px', fontSize: 24 }}>🕘 Attendance — Setup</h1>
      <p style={{ color: 'var(--text-muted)', marginTop: 0 }}>
        Onboard each employee once, then they are marked automatically as they walk past any entry or exit point.
        Unrecognised attempts are never marked — they are filed for review.
      </p>
      {err && <div style={{ ...card, borderColor: 'var(--danger)', color: 'var(--danger)' }}>{err}</div>}

      <section style={card}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 16 }}>
          <input type="checkbox" checked={!!cfg.enabled} disabled={busy} onChange={e => saveSettings({ enabled: e.target.checked })} />
          <b>Enable Attendance</b>
          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{cfg.enabled ? 'Active' : 'Off — nothing runs until enabled'}</span>
        </label>
        <div style={{ marginTop: 14, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <Field label="History retention (days)">
            <input type="number" min={1} defaultValue={cfg.retention_days} disabled={busy}
                   onBlur={e => saveSettings({ retention_days: Number(e.target.value) })} style={{ ...input, width: 90 }} />
          </Field>
          <Field label="Multi-camera dedup (s)">
            <input type="number" min={0} defaultValue={cfg.dedup_seconds ?? 120} disabled={busy}
                   onBlur={e => saveSettings({ dedup_seconds: Number(e.target.value) })} style={{ ...input, width: 100 }} />
          </Field>
          <Field label="Min face confidence">
            <input type="number" min={0.1} max={1} step="0.05" defaultValue={cfg.face_min_conf ?? 0.5} disabled={busy}
                   onBlur={e => saveSettings({ face_min_conf: Number(e.target.value) })} style={{ ...input, width: 100 }} />
          </Field>
          <Field label="Declare absent after">
            <input type="time" defaultValue={cfg.absent_after ?? '11:00'} disabled={busy}
                   onBlur={e => saveSettings({ absent_after: e.target.value })} style={input} />
          </Field>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.6 }}>
          <b>Dedup</b> is fleet-wide per person — being seen by two cameras inside this window is one punch, not two.<br />
          <b>Min face confidence</b> is the bar for an automatic mark. A weaker match is treated as unrecognised and sent to review, so it can never mark the wrong employee.<br />
          <b>Absent after</b> — before this time a no-show shows as Awaited rather than Absent.
        </div>
      </section>

      {!draft && <OnboardingSection cameras={opts.cameras} employees={emps}
                                    knownNames={opts.employees || []} onDone={load} />}

      {!draft && (
        <section style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ fontSize: 18, margin: 0 }}>Employees <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 13 }}>(optional — enables Present/Absent + report grouping)</span></h2>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setEmps([...emps, { name: '', employee_id: '', department: '' }])} style={btn}>+ Add</button>
              <button onClick={() => saveEmps(emps)} style={primaryBtn}>Save</button>
            </div>
          </div>
          {emps.length === 0
            ? <div style={{ ...card, color: 'var(--text-muted)' }}>No employee directory — Present/Absent falls back to all Known Faces.</div>
            : <div style={card}>
                {emps.map((e, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                    <select value={e.name} onChange={ev => setEmps(emps.map((x, j) => j === i ? { ...x, name: ev.target.value } : x))} style={{ ...input, minWidth: 160 }}>
                      <option value="">Name (Known Face)…</option>
                      {opts.employees.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <input placeholder="Employee ID" value={e.employee_id} onChange={ev => setEmps(emps.map((x, j) => j === i ? { ...x, employee_id: ev.target.value } : x))} style={{ ...input, width: 130 }} />
                    <input placeholder="Department" value={e.department} onChange={ev => setEmps(emps.map((x, j) => j === i ? { ...x, department: ev.target.value } : x))} style={{ ...input, width: 150 }} />
                    <button onClick={() => setEmps(emps.filter((_, j) => j !== i))} style={{ ...btn, color: 'var(--danger)', marginLeft: 'auto' }}>Remove</button>
                  </div>
                ))}
              </div>}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24 }}>
            <h2 style={{ fontSize: 18, margin: 0 }}>Attendance Points</h2>
            <button onClick={startNew} style={primaryBtn}>+ Add Point</button>
          </div>
          {(cfg.checkpoints || []).length === 0
            ? <div style={{ ...card, color: 'var(--text-muted)' }}>No attendance points yet.</div>
            : (cfg.checkpoints || []).map(cp => (
                <div key={cp.id} style={card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 600 }}>{cp.name} {!cp.active && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(inactive)</span>}</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>
                        {PURPOSE.find(p => p[0] === cp.purpose)?.[1] || cp.purpose} · {cp.schedule?.start}–{cp.schedule?.end} · presence {cp.min_presence_seconds}s
                        {cp.exception_on_gesture === false ? ' · no exceptions' : ' · hand-raise files exceptions'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <button onClick={() => startEdit(cp)} style={btn}>Edit</button>
                      <button onClick={() => del(cp)} style={{ ...btn, color: 'var(--danger)' }}>Delete</button>
                    </div>
                  </div>
                </div>
              ))}
        </section>
      )}

      {draft && (
        <section style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: 18 }}>{editId ? 'Edit Point' : 'New Attendance Point'}</h2>
          <div style={card}>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <Field label="Name"><input value={draft.name} onChange={e => setD({ name: e.target.value })} placeholder="e.g. Main Entrance" style={input} /></Field>
              <Field label="Camera">
                <select value={draft.camera_idx} onChange={e => setD({ camera_idx: e.target.value })} style={input}>
                  <option value="">Select camera…</option>
                  {opts.cameras.map(c => <option key={c.idx} value={c.idx}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Purpose">
                <select value={draft.purpose} onChange={e => setD({ purpose: e.target.value })} style={input}>
                  {PURPOSE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}><input type="checkbox" checked={draft.active} onChange={e => setD({ active: e.target.checked })} /> Active</label>
            </div>

            <div style={{ marginTop: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <Field label="Start"><input type="time" value={draft.schedule.start} onChange={e => setS({ start: e.target.value })} style={input} /></Field>
              <Field label="End"><input type="time" value={draft.schedule.end} onChange={e => setS({ end: e.target.value })} style={input} /></Field>
              <Field label="Min presence (s)"><input type="number" min={0} step="0.5" value={draft.min_presence_seconds} onChange={e => setD({ min_presence_seconds: Number(e.target.value) })} style={{ ...input, width: 100 }} /></Field>
              <Field label="Cooldown (s)"><input type="number" min={0} value={draft.cooldown_seconds} onChange={e => setD({ cooldown_seconds: Number(e.target.value) })} style={{ ...input, width: 100 }} /></Field>
              <Field label="Hand-raise hold (s)"><input type="number" min={0} step="0.5" value={draft.gesture_hold_seconds} onChange={e => setD({ gesture_hold_seconds: Number(e.target.value) })} style={{ ...input, width: 110 }} /></Field>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, alignSelf: 'flex-end' }}>
                <input type="checkbox" checked={draft.exception_on_gesture !== false} onChange={e => setD({ exception_on_gesture: e.target.checked })} /> Hand-raise files an exception
              </label>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.6 }}>
              Recognised employees are marked on the walk-through — no hand raise, no waiting.
              Min presence is just one confirming frame inside the zone; keep it low and draw the zone wide
              enough to cover about two seconds of walking.
              {draft.exception_on_gesture === false &&
                <><br /><b style={{ color: 'var(--warning)' }}>Exceptions off:</b> an employee the camera fails to recognise has no way to flag it here.</>}
            </div>

            <div style={{ marginTop: 14, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <Field label="Mark late after (HH:MM)"><input type="time" value={draft.late_after} onChange={e => setD({ late_after: e.target.value })} style={input} /></Field>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, alignSelf: 'flex-end' }}><input type="checkbox" checked={draft.speaker_enabled} onChange={e => setD({ speaker_enabled: e.target.checked })} /> Speaker confirmation</label>
              {draft.speaker_enabled && <>
                <Field label="Speaker">
                  <select value={draft.speaker_target} onChange={e => setD({ speaker_target: e.target.value })} style={input}>
                    {(opts.speakers || [{ id: 'camera', name: 'Checkpoint camera speaker' }]).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </Field>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, alignSelf: 'flex-end' }}><input type="checkbox" checked={draft.speaker_name} onChange={e => setD({ speaker_name: e.target.checked })} /> Announce name</label>
              </>}
            </div>

            <div style={{ marginTop: 12 }}>
              <div style={label}>Active days</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {DAYS.map((d, i) => <button key={d} type="button" onClick={() => toggleDay(i)} style={chip(draft.schedule.active_days.includes(i))}>{d}</button>)}
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <div style={label}>Attendance zone {draft.camera_idx === '' && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>— select a camera first</span>}</div>
              {draft.camera_idx !== '' && <PatrolZoneEditor cameraIdx={draft.camera_idx} value={draft.zones[0] || []} onChange={setZone} />}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <button onClick={save} disabled={busy} style={primaryBtn}>{busy ? 'Saving…' : 'Save Point'}</button>
            <button onClick={cancel} disabled={busy} style={btn}>Cancel</button>
          </div>
        </section>
      )}
    </div>
  );
}

// ── Onboarding ────────────────────────────────────────────────────────────────
// One-time face enrolment with a live hand-raise check. The gesture is a liveness
// and consent signal: without it, whoever happens to be walking past the camera
// can be enrolled under someone else's name. Nobody is marked automatically
// until they have been through this (or had an exception approved, which is the
// same vouching done from the evidence photo instead of in person).
function OnboardingSection({ cameras, employees, knownNames, onDone }) {
  const [open, setOpen]   = useState(false);
  const [cam, setCam]     = useState('');
  // Pick an EXISTING identity or explicitly declare a new one. A free-text name
  // was how "sagar" and "SagarRaina" became two people for one face: the matcher
  // kept returning the old name, which was not in the employee directory, so
  // every sighting was refused and filed as an exception. Choosing from a list
  // makes "add another capture" the easy path and "create a person" deliberate.
  const [pick, setPick]   = useState('');
  const [newName, setNewName] = useState('');
  const [empId, setEmpId] = useState('');
  const [dept, setDept]   = useState('');
  const isNew = pick === '__new__';
  const name  = isNew ? newName.trim() : pick;
  const existing = (employees || []).find(e => e.name === name) || null;

  // Prefill ID/department when adding to someone already in the directory —
  // retyping them is how they get silently blanked.
  useEffect(() => {
    if (isNew) { setEmpId(''); setDept(''); return; }
    const e = (employees || []).find(x => x.name === pick);
    setEmpId(e?.employee_id || ''); setDept(e?.department || '');
  }, [pick]); // eslint-disable-line react-hooks/exhaustive-deps
  const [readout, setReadout] = useState(null);
  const [busy, setBusy]   = useState(false);
  const [msg, setMsg]     = useState(null);   // { ok, text }
  const hasCam = cam !== '';

  // Live readout so the admin can see the hand is up and a face is visible
  // BEFORE pressing Enrol — otherwise every attempt is a blind 422.
  useEffect(() => {
    if (!open || !hasCam) { setReadout(null); return; }
    let alive = true, working = false;
    const poll = async () => {
      if (working) return;
      working = true;
      try {
        const r = await apiFetch('/ui/patrol/test', { method: 'POST', json: { camera_idx: Number(cam), polygon: [] } });
        if (alive) setReadout(r.ok ? await r.json() : null);
      } catch { /* transient */ } finally { working = false; }
    };
    poll();
    const t = setInterval(poll, 800);
    return () => { alive = false; clearInterval(t); };
  }, [open, cam, hasCam]);

  const raised = !!(readout?.left_raised || readout?.right_raised);

  const enrol = async () => {
    if (!name) { setMsg({ ok: false, text: isNew ? 'Enter a name.' : 'Select who this is.' }); return; }
    setBusy(true); setMsg(null);
    try {
      const r = await apiFetch('/ui/attendance/onboard', {
        method: 'POST',
        json: { name, camera_idx: Number(cam), employee_id: empId, department: dept },
      });
      if (!r.ok) {
        const d = await r.json().catch(() => null);
        throw new Error(d?.detail || `Failed (${r.status})`);
      }
      setMsg({ ok: true, text: existing
        ? `Another capture added for ${name}. More captures from the angles people actually walk past make recognition steadier.`
        : `${name} onboarded — they will be marked automatically from now on.` });
      setNewName('');
      onDone?.();
    } catch (e) {
      setMsg({ ok: false, text: e.message || 'Enrolment failed.' });
    } finally { setBusy(false); }
  };

  const pending = (employees || []).filter(e => e.name && !e.onboarded_at);

  return (
    <section style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <b style={{ fontSize: 16 }}>Onboarding</b>
          <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 2 }}>
            Enrol an employee's face once, with a hand raise to confirm they are really there.
          </div>
        </div>
        <button onClick={() => setOpen(o => !o)} style={open ? btn : primaryBtn}>{open ? 'Close' : '+ Onboard employee'}</button>
      </div>

      {pending.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 13, color: 'var(--warning)' }}>
          ⚠ Not yet onboarded — they will never be marked automatically: {pending.map(e => e.name).join(', ')}
        </div>
      )}

      {open && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <Field label="Camera">
              <select value={cam} onChange={e => setCam(e.target.value)} style={input}>
                <option value="">Select camera…</option>
                {(cameras || []).map(c => <option key={c.idx} value={c.idx}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Who">
              <select value={pick} onChange={e => setPick(e.target.value)} style={{ ...input, minWidth: 190 }}>
                <option value="">Select…</option>
                {(knownNames || []).length > 0 && <optgroup label="Add another capture">
                  {(knownNames || []).map(n => <option key={n} value={n}>{n}</option>)}
                </optgroup>}
                <option value="__new__">+ New person…</option>
              </select>
            </Field>
            {isNew && <Field label="Name"><input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Full name" style={input} /></Field>}
            <Field label="Employee ID"><input value={empId} onChange={e => setEmpId(e.target.value)} style={{ ...input, width: 130 }} /></Field>
            <Field label="Department"><input value={dept} onChange={e => setDept(e.target.value)} style={{ ...input, width: 150 }} /></Field>
          </div>

          {hasCam && (
            <div style={{ marginTop: 14, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <OnboardPreview cameraIdx={cam} readout={readout} />
              <div style={{ flex: '0 0 230px', minWidth: 210 }}>
                <ORow label="Person detected" ok={readout?.person_detected} value={readout?.person_detected ? 'YES' : 'NO'} />
                <ORow label="Hand raised"     ok={raised}                   value={raised ? 'RAISED' : 'NOT RAISED'} />
                <div style={{
                  marginTop: 12, padding: '10px 14px', borderRadius: 10, textAlign: 'center', fontWeight: 800,
                  background: raised ? 'rgba(92,201,138,0.92)' : 'rgba(40,40,40,0.6)',
                  color: raised ? '#08130c' : 'var(--text-muted)',
                  border: `1px solid ${raised ? '#5cc98a' : 'var(--border)'}`,
                }}>{raised ? 'READY' : 'RAISE A HAND'}</div>
                <button onClick={enrol} disabled={busy || !raised || !name}
                        style={{ ...primaryBtn, width: '100%', marginTop: 10, padding: '10px 16px',
                                 opacity: (busy || !raised || !name) ? 0.5 : 1 }}>
                  {busy ? 'Enrolling…' : (existing || (!isNew && pick) ? 'Add capture' : 'Enrol face')}
                </button>
                {!isNew && pick && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, textAlign: 'center' }}>
                    Adding to <b>{pick}</b>{existing?.onboarded_at ? ' — already onboarded' : ''}.
                    Captures accumulate under one identity; near-identical ones are rejected.
                  </div>
                )}
              </div>
            </div>
          )}

          {msg && (
            <div style={{ ...card, marginTop: 12, borderColor: msg.ok ? 'var(--accent)' : 'var(--danger)',
                          color: msg.ok ? 'var(--text)' : 'var(--danger)' }}>{msg.text}</div>
          )}
        </div>
      )}
    </section>
  );
}

function OnboardPreview({ cameraIdx, readout }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    let alive = true;
    const tick = () => apiImageUrl(`/cameras/${cameraIdx}/frame?ts=${Date.now()}`)
      .then(u => { if (alive) setUrl(u); }).catch(() => {});
    tick();
    const t = setInterval(tick, 800);
    return () => { alive = false; clearInterval(t); };
  }, [cameraIdx]);
  const raised = !!(readout?.left_raised || readout?.right_raised);
  return (
    <div style={{ flex: '1 1 300px', maxWidth: 480, background: '#000', borderRadius: 8, overflow: 'hidden',
                  border: `2px solid ${raised ? '#5cc98a' : 'var(--border)'}` }}>
      {url ? <img src={url} alt="onboarding" style={{ display: 'block', width: '100%', height: 'auto' }} />
           : <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>Loading camera…</div>}
    </div>
  );
}

function ORow({ label, value, ok }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{label}</span>
      <span style={{ fontWeight: 700, fontSize: 15, color: ok ? '#5cc98a' : 'var(--text)' }}>{value}</span>
    </div>
  );
}

function Field({ label, children }) {
  return <div><div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>{children}</div>;
}
const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginTop: 12 };
const label = { fontSize: 13, fontWeight: 600, marginBottom: 8 };
const input = { padding: '8px 10px', background: 'var(--surface-alt)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14 };
const btn = { padding: '7px 14px', background: 'var(--surface-alt)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', fontSize: 14 };
const primaryBtn = { ...btn, background: 'var(--primary)', color: 'var(--primary-light)', border: 'none', fontWeight: 600 };
const chip = active => ({ display: 'inline-flex', alignItems: 'center', padding: '6px 12px', borderRadius: 20, cursor: 'pointer', fontSize: 13, border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`, background: active ? 'var(--primary-muted)' : 'transparent', color: 'var(--text)' });
